import React, { useState } from 'react';
import { Divider, List, ListItem, ListItemText, TextField, Typography } from '@mui/material';

import { TextOperationService } from '../../../services';
import type { CritiqueSuggestion, TextOperationResult } from '../../../domain';
import {
  DEFAULT_CRITIQUE_TEXT_STATE,
  buildCritiqueTextRequest,
  type CritiqueTextFormState,
} from './buildRequests';
import { useRunTextOperation } from './useTextOperationMutation';
import { TEXT_OPERATION_CATALOG } from './operationCatalog';
import { PanelScaffold } from './PanelScaffold';
import type { TextPanelProps } from './panelProps';

export const CritiquePanel: React.FC<TextPanelProps> = ({
  ensureSession,
  isCreatingSession = false,
  documentId,
  sectionId,
  onResult,
}) => {
  const [state, setState] = useState<CritiqueTextFormState>(DEFAULT_CRITIQUE_TEXT_STATE);
  const [suggestions, setSuggestions] = useState<CritiqueSuggestion[]>([]);

  // Critique never edits the document — it surfaces feedback instead.
  const { run, mutation, validationError } = useRunTextOperation(
    TextOperationService.critique,
    ensureSession,
    documentId,
    {
      insert: false,
      onResult: (result: TextOperationResult) => {
        setSuggestions(result.result?.suggestions ?? []);
        onResult?.(result);
      },
    },
  );

  const needsDocument = !documentId;
  const handleSubmit = () => run((sid, docId) => buildCritiqueTextRequest(state, sid, docId, sectionId));

  return (
    <PanelScaffold
      meta={TEXT_OPERATION_CATALOG.Critique}
      needsDocument={needsDocument}
      validationError={validationError}
      mutationError={mutation.isError ? mutation.error : null}
      busy={mutation.isPending}
      starting={isCreatingSession}
      submitDisabled={mutation.isPending || isCreatingSession || needsDocument}
      submitLabel="Critique"
      busyLabel="Reviewing…"
      onSubmit={handleSubmit}
      footer={
        suggestions.length > 0 ? (
          <>
            <Divider />
            <Typography variant="overline" color="text.secondary">
              Feedback
            </Typography>
            <List dense disablePadding>
              {suggestions.map((s, i) => (
                <ListItem key={i} alignItems="flex-start" disableGutters>
                  <ListItemText
                    primary={`${s.category}: ${s.comment}`}
                    secondary={s.suggestion}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
              ))}
            </List>
          </>
        ) : undefined
      }
    >
      <TextField
        label="Aspects (optional, comma-separated)"
        value={state.aspects}
        onChange={e => setState(s => ({ ...s, aspects: e.target.value }))}
        size="small"
        fullWidth
        placeholder="pacing, dialogue, clarity"
      />
    </PanelScaffold>
  );
};
